import { IMediaInstance, PlayOptions, Sound, sound as soundLibrary } from '@pixi/sound'
import { Assets } from 'pixi.js'
import { isXinStarDemoMode } from '../customize'

export interface SoundData {
  alias: string
  src: string
  volume?: number
}

interface LoadSoundOptions {
  customStorageKey?: string
}

export enum SoundType {
  bgm = 'bgm_mg',
  buttonConfirm = 'button_open',
  buttonClose = 'button_close',
  spin = 'spin',
  stake_plus = 'stake_plus',
  stake_minus = 'stake_minus',
}

let STORAGE_KEY = `slot_isMute`

class SoundManager {
  private currentBGM: string
  private isAudioUnlocked = false
  private isTabHidden = false

  private visibilityChangeCallback = () => {
    this.isTabHidden = document.hidden

    if (this.isTabHidden) {
      this.mute({
        setFlag: false,
      })
    }
    else {
      if (!this.isMute()) {
        this.unmute({
          setFlag: false,
        })
      }
    }
  }

  private unlockAudio = () => {
    if (this.isAudioUnlocked) {
      return
    }

    this.isAudioUnlocked = true
  }

  private findSoundIfExists(alias: string | SoundType) {
    if (!soundLibrary.exists(alias)) {
      return undefined
    }
    return soundLibrary.find(alias)
  }

  async load(manifest: SoundData[], options: LoadSoundOptions = {}) {
    this.initSetting(options)
    //
    await Promise.all(manifest.map(config =>
      new Promise<void>((resolve) => {
        if (soundLibrary.exists(config.alias)) {
          resolve()
          return
        }
        soundLibrary.add(config.alias, {
          volume: config.volume || 1,
          url: config.src,
          preload: true,
          autoPlay: this.isMute() ? false : config.alias === this.currentBGM,
          loaded: () => resolve(),
          loop: config.alias === this.currentBGM,
        })
      }),
    ))
  }

  private initSetting(options: LoadSoundOptions) {
    STORAGE_KEY = options.customStorageKey ? `${options.customStorageKey}_isMute` : STORAGE_KEY
    if (isXinStarDemoMode()) {
      STORAGE_KEY = `demo_${STORAGE_KEY}`
    }

    soundLibrary.disableAutoPause = true
    this.registerVisibilityListener()
    this.registerAudioUnlockListener()

    const isMute = this.isMute()
    if (isMute) soundLibrary.muteAll()
    this.currentBGM = SoundType.bgm
  }

  loadFromAssetPack(aliasArr: string[], options: LoadSoundOptions = {}) {
    this.initSetting(options)
    this.loadSounds(aliasArr)
    const bgmSound = this.findSoundIfExists(this.currentBGM)
    if (!bgmSound) {
      return
    }
    bgmSound.loop = true
    if (!this.isMute()) bgmSound.autoPlay = true
  }

  loadSounds(aliasArr: string[]) {
    aliasArr.forEach((alias) => {
      if (soundLibrary.exists(alias)) {
        return
      }

      try {
        const sound: Sound = Assets.get(alias)
        soundLibrary.add(alias, sound)
      }
      catch (_) {
        //
      }
    })
  }

  switchBGM(newBGM: string | SoundType) {
    const bgmSound = this.findSoundIfExists(this.currentBGM)
    bgmSound?.stop()
    this.currentBGM = newBGM
  }

  stopBGM() {
    this.findSoundIfExists(this.currentBGM)?.stop()
  }

  playBGM(options?: Omit<PlayOptions, 'loop'>) {
    try {
      const newSound = this.findSoundIfExists(this.currentBGM)
      if (!newSound) {
        return
      }
      const volume = options?.volume || newSound.volume
      const bgmOptions: PlayOptions = options
        ? { ...options, loop: true, volume }
        : {
            loop: true,
            volume,
          }
      newSound.play(bgmOptions)
    }
    catch (_) {
      //
    }
  }

  play(alias: string | SoundType, options?: PlayOptions): IMediaInstance | undefined {
    try {
      if (!this.isAudioUnlocked) {
        console.warn('Audio is not unlocked yet. Please interact with the page first.')
        return
      }
      const sound = this.findSoundIfExists(alias)
      if (!sound) {
        return
      }
      const volume = options?.volume || sound.volume
      return sound.play({ ...options, volume }) as IMediaInstance
    }
    catch (_) {
      //
    }
  }

  stop(alias: string | SoundType) {
    if (!soundLibrary.exists(alias)) {
      return
    }
    soundLibrary.stop(alias)
  }

  mute(options = { setFlag: true }) {
    soundLibrary.muteAll()
    if (options.setFlag)
      window.localStorage.setItem(STORAGE_KEY, 'true')
  }

  unmute(options = { setFlag: true }) {
    soundLibrary.unmuteAll()

    const bgm = this.findSoundIfExists(this.currentBGM)
    if (bgm && !bgm.isPlaying) {
      bgm.play()
    }

    if (options.setFlag)
      window.localStorage.setItem(STORAGE_KEY, 'false')
  }

  isMute() {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  }

  registerAudioUnlockListener() {
    const state = soundLibrary.context.audioContext.state
    if (state === 'running') {
      this.isAudioUnlocked = true
      return
    }

    window.removeEventListener('pointerdown', this.unlockAudio)
    window.removeEventListener('keydown', this.unlockAudio)
    window.addEventListener('pointerdown', this.unlockAudio, { once: true })
    window.addEventListener('keydown', this.unlockAudio, { once: true })
  }

  registerVisibilityListener() {
    window.removeEventListener('visibilitychange', this.visibilityChangeCallback)
    window.addEventListener('visibilitychange', this.visibilityChangeCallback)
  }
}

export const soundManager = new SoundManager()
